/**
 * BB-14d1 -- the browser media leg's state machine.
 *
 * Pure and dependency-free on purpose: it imports neither SIP.js nor
 * React, so the legal transitions can be tested without constructing a
 * SIP client at all. `mediaLegClient` owns the SIP objects and asks this
 * module what a given event means.
 *
 * The states mirror the BB-14d slice boundaries exactly:
 *
 *   DISCONNECTED -> CONNECTING -> REGISTERED -> JOINING -> MEDIA_READY
 *
 * d1 builds the machine and never leaves DISCONNECTED. d2 earns
 * CONNECTING/REGISTERED by opening the WebSocket and registering. d3
 * earns JOINING/MEDIA_READY by joining ConfBridge. BB-14e gates native
 * READY on MEDIA_READY.
 *
 * DEGRADED is recoverable, FAILED is terminal. The distinction matters:
 * BB-14e must drop the agent out of READY for either, but only FAILED
 * requires re-issuing an identity.
 */

export const MediaLegState = Object.freeze({
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  REGISTERED: 'REGISTERED',
  JOINING: 'JOINING',
  MEDIA_READY: 'MEDIA_READY',
  DEGRADED: 'DEGRADED',
  FAILED: 'FAILED',
});

export const MediaLegEvent = Object.freeze({
  CONNECT_REQUESTED: 'CONNECT_REQUESTED',
  REGISTERED: 'REGISTERED',
  JOIN_REQUESTED: 'JOIN_REQUESTED',
  MEDIA_CONFIRMED: 'MEDIA_CONFIRMED',
  MEDIA_IMPAIRED: 'MEDIA_IMPAIRED',
  MEDIA_RECOVERED: 'MEDIA_RECOVERED',
  /** Another tab won the lease. Always terminal for this tab. */
  SUPERSEDED: 'SUPERSEDED',
  /** Operator or navigation teardown. Always returns to DISCONNECTED. */
  TEARDOWN: 'TEARDOWN',
  FAILURE: 'FAILURE',
});

/**
 * A media leg is only safe to carry a call in MEDIA_READY. BB-14e must
 * consult this rather than re-deriving the rule, so the "no READY
 * without healthy media" invariant has exactly one definition.
 */
export function isMediaHealthy(state) {
  return state === MediaLegState.MEDIA_READY;
}

/** Terminal states cannot be advanced; the leg must be rebuilt. */
export function isTerminal(state) {
  return state === MediaLegState.FAILED;
}

const TRANSITIONS = Object.freeze({
  [MediaLegState.DISCONNECTED]: {
    [MediaLegEvent.CONNECT_REQUESTED]: MediaLegState.CONNECTING,
  },
  [MediaLegState.CONNECTING]: {
    [MediaLegEvent.REGISTERED]: MediaLegState.REGISTERED,
  },
  [MediaLegState.REGISTERED]: {
    [MediaLegEvent.JOIN_REQUESTED]: MediaLegState.JOINING,
    [MediaLegEvent.MEDIA_IMPAIRED]: MediaLegState.DEGRADED,
  },
  [MediaLegState.JOINING]: {
    [MediaLegEvent.MEDIA_CONFIRMED]: MediaLegState.MEDIA_READY,
    [MediaLegEvent.MEDIA_IMPAIRED]: MediaLegState.DEGRADED,
  },
  [MediaLegState.MEDIA_READY]: {
    [MediaLegEvent.MEDIA_IMPAIRED]: MediaLegState.DEGRADED,
  },
  [MediaLegState.DEGRADED]: {
    [MediaLegEvent.MEDIA_RECOVERED]: MediaLegState.MEDIA_READY,
  },
  [MediaLegState.FAILED]: {},
});

/**
 * `(state, event) -> { state, reason, changed }`.
 *
 * Never throws on an unexpected event: a SIP stack emits events in
 * orders a state chart does not anticipate, and a thrown error inside a
 * transport callback would leave the leg wedged. An event with no legal
 * transition is reported as `changed: false` and the caller decides
 * whether that is worth surfacing.
 */
export function reduceMediaLegState(state, event, reason = null) {
  // TEARDOWN and SUPERSEDED are universal: they must work from every
  // state including FAILED, because both mean "stop using this leg".
  if (event === MediaLegEvent.TEARDOWN) {
    return {
      state: MediaLegState.DISCONNECTED,
      reason: reason || 'teardown',
      changed: state !== MediaLegState.DISCONNECTED,
    };
  }
  if (event === MediaLegEvent.SUPERSEDED) {
    return {
      state: MediaLegState.FAILED,
      reason: reason || 'superseded_by_newer_lease',
      changed: state !== MediaLegState.FAILED,
    };
  }
  if (event === MediaLegEvent.FAILURE) {
    return {
      state: MediaLegState.FAILED,
      reason: reason || 'failure',
      changed: state !== MediaLegState.FAILED,
    };
  }

  const next = (TRANSITIONS[state] || {})[event];
  if (!next) {
    return { state, reason, changed: false };
  }
  return { state: next, reason, changed: true };
}

/**
 * Lease-generation fencing. Last writer wins: a strictly greater
 * generation supersedes this tab.
 *
 * Compared as numbers, and a non-numeric or absent observed generation
 * is *not* treated as superseding -- a malformed signal must not be able
 * to tear down a healthy leg.
 */
export function isSupersededBy(ownGeneration, observedGeneration) {
  if (typeof ownGeneration !== 'number' || !Number.isFinite(ownGeneration)) {
    return false;
  }
  if (typeof observedGeneration !== 'number' || !Number.isFinite(observedGeneration)) {
    return false;
  }
  return observedGeneration > ownGeneration;
}
